// Packaging a shared trip's photos into one zip, in the browser.
//
// No backend at all: the page already holds a presigned GET URL for every photo
// it can show, and the attachments bucket answers a cross-origin fetch of one
// (see photoDownloads.mjs's header for why that is the right transport and what
// the alternative would have cost). zipStore.mjs owns the container format.
//
// HOW MEMORY IS BOUNDED, which is the whole design and the one thing not to
// "simplify":
//
//   - The fetches are SEQUENTIAL. A Promise.all over 240 photos pulls every
//     original into the JS heap at once -- a gigabyte on a phone -- and is the
//     version that crashes the tab. photoZip.test.js asserts the second fetch
//     has not started before the first resolves, so that regression can't
//     come back quietly.
//   - Each photo's bytes are handed to a Blob the instant they arrive and the
//     ArrayBuffer is dropped. A Blob is browser-managed storage and a large one
//     spills to disk, so the JS heap only ever holds ONE photo, not the archive.
//   - The final `new Blob(parts)` REFERENCES its parts; it does not copy them.
//
// The byte total is known before any of this runs, because the shared payload
// carries `size` per photo -- so the button can state the cost up front and this
// function can refuse politely rather than discovering the problem at photo 180.

import { downloadBlob } from './downloadFile.mjs';
import { expiredPhotoError } from './photoDownloads.mjs';
import { displayPhotoName } from '../fleet-shared/Attachments.mjs';
import {
  zipEntry,
  zipCentralDirectory,
  uniqueZipName,
  zipSafeName,
} from './zipStore.mjs';

// The ceiling on one archive.
//
// It is a MEMORY and patience limit, not a format one: zipStore is 32-bit and so
// caps at 4 GiB, but a download that big fails for a dozen other reasons first.
// A gigabyte is already generous for a trip's photos (240 photos, the shared
// payload's own cap, averaging 4 MB each).
export const MAX_ZIP_BYTES = 1024 * 1024 * 1024;

// Sum of what a set of photos will weigh. Free -- the presign payload carries
// `size` for each one, which is also what AttachmentImage's placeholder shows.
export function zipBytesTotal(groups) {
  let total = 0;
  for (const group of groups ?? []) {
    for (const photo of group.photos ?? []) {
      total += Number(photo.size) || 0;
    }
  }
  return total;
}

export function zipPhotoCount(groups) {
  let count = 0;
  for (const group of groups ?? []) {
    count += (group.photos ?? []).length;
  }
  return count;
}

// `Gulf Islands 2026` -> `gulf-islands-2026-photos.zip`.
export function zipFileName(tripName) {
  const slug = String(tripName ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
    .replace(/-$/, '');
  return `${slug || 'trip'}-photos.zip`;
}

// The name one photo takes inside the archive. A folder per day, because two
// days of a trip routinely both hold IMG_1234.jpg and because a flat pile of 200
// photos is not an archive anyone can use. `folder` absent (the single-entry
// page) gives a flat zip.
function entryNameFor(group, photo) {
  const name = zipSafeName(displayPhotoName(photo.name || 'photo.jpg'));
  return group.folder ? `${zipSafeName(group.folder)}/${name}` : name;
}

/**
 * Build the zip and hand it to the browser.
 *
 * `groups` is `[{ folder, photos: [{ key, name, size, url }] }]`, in the order
 * they should appear.
 *
 * Resolves `{ packed, skipped, bytes, stoppedAtCap }`. `stoppedAtCap` is how the
 * caller says "40 of 96 photos are in this file" -- going over the cap stops the
 * loop in order rather than silently dropping an arbitrary subset, so the result
 * is at least predictable and the message can be honest.
 */
export async function downloadPhotosAsZip(
  groups,
  {
    zipName = 'photos.zip',
    onProgress,
    signal,
    fetchImpl = fetch,
    // The cap is a parameter rather than a hard-coded read of MAX_ZIP_BYTES so a
    // caller can be stricter -- and so its behaviour can be tested without
    // allocating a gigabyte to prove a comparison.
    maxBytes = MAX_ZIP_BYTES,
  } = {}
) {
  const items = [];
  for (const group of groups ?? []) {
    for (const photo of group.photos ?? []) {
      if (photo?.url) items.push({ name: entryNameFor(group, photo), photo });
    }
  }

  if (items.length === 0) {
    throw new Error('There are no photos to download.');
  }

  const parts = [];
  const records = [];
  const seen = new Set();
  let offset = 0;
  let packed = 0;
  let stoppedAtCap = false;

  for (const item of items) {
    if (signal?.aborted) throw abortError();

    if (offset >= maxBytes) {
      stoppedAtCap = true;
      break;
    }

    const bytes = await fetchPhotoBytes(item.photo, { signal, fetchImpl });

    // Checked against the REAL length rather than the payload's `size`, so a
    // mis-stated size can't walk us past the cap.
    if (offset + bytes.length > maxBytes && packed > 0) {
      stoppedAtCap = true;
      break;
    }

    const { header, record } = zipEntry({
      name: uniqueZipName(item.name, seen),
      bytes,
      offset,
    });

    // The Blob takes ownership here and the Uint8Array goes out of scope on the
    // next iteration. This line is the memory bound.
    parts.push(new Blob([header, bytes]));
    records.push(record);
    offset += header.length + bytes.length;
    packed += 1;

    if (onProgress) onProgress({ done: packed, total: items.length, bytes: offset });
  }

  parts.push(new Blob([zipCentralDirectory(records, offset)]));
  downloadBlob(zipName, new Blob(parts, { type: 'application/zip' }));

  return {
    packed,
    skipped: items.length - packed,
    bytes: offset,
    stoppedAtCap,
  };
}

function abortError() {
  const err = new Error('Download cancelled.');
  err.name = 'AbortError';
  return err;
}

async function fetchPhotoBytes(photo, { signal, fetchImpl }) {
  // `cache: 'reload'` for the reason photoDownloads.mjs sets out at length: the
  // thumbnail's <img> leaves a no-CORS response in the cache that a fetch of the
  // same URL cannot use, and here one such entry would fail the whole zip.
  const response = await fetchImpl(photo.url, { signal, cache: 'reload' });
  if (!response.ok) {
    if (response.status === 403 || response.status === 404) {
      throw expiredPhotoError(response.status);
    }
    throw new Error(`Could not download ${photo.name || 'a photo'} (${response.status}).`);
  }
  return new Uint8Array(await response.arrayBuffer());
}
